import { type IntlShape } from 'react-intl'
import { isEnumMember } from '../../../helpers/types'
import {
  ActionType,
  DateFormat,
  DateTimeFormat,
  type FilterCondition,
  GeoMatch,
  type GeoSpec,
  GroupMatch,
  type GroupSpec,
  NumericFormat,
  SpecType,
  type TrackingSpec,
} from '../../../types/FilterCondition'
import {
  UiActivityDateType,
  type UiActivityDateValue,
  type UiBlock,
  UiChannelType,
  UiConditionType,
  type UiFilterCondition,
  type UiFormat,
  type UiGenericSpec,
  UiTextFormat,
  UiTrackingConditionTypes,
} from '../filterConditionTypes'
import {
  getUiEmailCondTypeBasedOnSpec,
  getUiSmsCondTypeBasedOnSpec,
  getUiSubscriptionCondTypeBasedOnSpec,
} from './apimapping/activityApiMapping'

const isTrackingSpec = (
  spec: FilterCondition['Spec'],
): spec is TrackingSpec => {
  return (
    typeof spec === 'object' &&
    spec !== null &&
    'TrackingActive' in spec &&
    typeof spec.TrackingActive === 'boolean'
  )
}

export const getCondTypeBasedOnSpec = (
  condition: FilterCondition,
): UiConditionType | undefined => {
  if (isTrackingSpec(condition.Spec)) {
    return condition.Spec.TrackingActive
      ? UiTrackingConditionTypes.TrackingActive
      : UiTrackingConditionTypes.TrackingAnonymous
  }

  switch (condition.SpecType) {
    case SpecType.GroupSpec:
      if (
        (condition.Spec as GroupSpec).GroupMatchOperator ===
        GroupMatch.NotBelongsTo
      ) {
        return UiConditionType.GroupNotBelongs
      }
      return UiConditionType.GroupBelongs
    case SpecType.TextSpec:
    case SpecType.NumericSpec:
    case SpecType.DateSpec:
    case SpecType.DateTimeSpec:
      return UiConditionType.ProfileField

    case SpecType.SubscriptionSpec:
      return getUiSubscriptionCondTypeBasedOnSpec(condition)
    case SpecType.EmailSpec:
      return getUiEmailCondTypeBasedOnSpec(condition)
    case SpecType.SmsSpec:
      return getUiSmsCondTypeBasedOnSpec(condition)

    case SpecType.EngagementSpec:
      return UiConditionType.Engagement
    case SpecType.GeoSpec:
      if (
        (condition.Spec as GeoSpec).GeoMatchOperator === GeoMatch.NotLocatedIn
      ) {
        return UiConditionType.GeoNotLocatedIn
      }
      return UiConditionType.GeoLocatedIn
    case SpecType.SysSpec:
    case SpecType.DeviceSpec:
      return UiConditionType.Device
    default:
      return undefined
  }
}

export const getDefaultSpecByConditionType = (
  condType: UiConditionType,
): Partial<UiGenericSpec> | undefined => {
  switch (condType) {
    case UiConditionType.GroupBelongs:
    case UiConditionType.GroupNotBelongs:
      return {
        Values: [],
      }
    case UiConditionType.ProfileField:
      return {
        // default, we still don't know the spectype at this stage
        Identifier: undefined,
        Format: undefined,
        Match: undefined,
        Value: undefined,
      }

    case UiConditionType.EmailSubscribed:
    case UiConditionType.EmailUnsubscribed:
      return {
        DateType: undefined,
        DateValue: undefined,
      }
    case UiConditionType.EmailSent:
    case UiConditionType.EmailSuccessfullySent:
    case UiConditionType.EmailNotSent:
    case UiConditionType.EmailOpened:
    case UiConditionType.EmailNotOpened:
    case UiConditionType.EmailBouncedHB:
    case UiConditionType.EmailBouncedSB:
    case UiConditionType.EmailNotBouncedHB:
    case UiConditionType.EmailPerformedAtLeast1Action:
    case UiConditionType.EmailPerformedLessThan5Actions:
    case UiConditionType.EmailPerformedLessThan10Actions:
    case UiConditionType.EmailPerformed10OrMoreActions:
      return {
        ChannelType: UiChannelType.Email,
        DateType: undefined,
        DateValue: undefined,
        Message: undefined,
        Tags: [],
      }
    case UiConditionType.SmsSent:
    case UiConditionType.SmsNotSent:
    case UiConditionType.SmsSuccessfullySent:
    case UiConditionType.SmsNotReceived:
    case UiConditionType.SmsExpired:
    case UiConditionType.SmsRejected:
      return {
        ChannelType: UiChannelType.Sms,
        DateType: undefined,
        DateValue: undefined,
        Message: undefined,
        Tags: [],
      }
    case UiConditionType.EmailClicked:
    case UiConditionType.EmailNotClicked:
      return {
        ChannelType: UiChannelType.Email,
        DateType: undefined,
        DateValue: undefined,
        Message: undefined,
        Tags: [],
        Link: '',
      }

    case UiConditionType.Engagement:
      return {
        Value: undefined,
      }

    case UiConditionType.GeoLocatedIn:
      return {
        GeoMatchOperator: 'LocatedIn',
        Country: '',
        Region: '',
        Precision: 2,
      }
    case UiConditionType.GeoNotLocatedIn:
      return {
        GeoMatchOperator: 'NotLocatedIn',
        Country: '',
        Region: '',
        Precision: 2,
      }

    case UiConditionType.Device:
      return {
        Value: undefined,
      }

    case UiTrackingConditionTypes.TrackingActive:
      return {
        Identifier: 'Tracking',
        TrackingActive: true,
        TrackingMatchOperator: undefined,
        Dates: undefined,
      }

    case UiTrackingConditionTypes.TrackingAnonymous:
      return {
        Identifier: 'Tracking',
        TrackingActive: false,
        TrackingMatchOperator: undefined,
        Dates: undefined,
      }

    default:
      return undefined
  }
}

export const getDefaultValueByFormat = (
  format: UiFormat,
): string | number | undefined => {
  if (isEnumMember<UiTextFormat>(format, UiTextFormat)) {
    return undefined
  }
  if (isEnumMember<NumericFormat>(format, NumericFormat)) {
    return 0
  }
  if (isEnumMember<DateFormat>(format, DateFormat)) {
    return undefined
  }
  if (isEnumMember<DateTimeFormat>(format, DateTimeFormat)) {
    return 0
  }
}

export const getEmptyCondition = (condIndex = 0): UiFilterCondition => {
  return {
    JoinType: getDefaultJoinType(condIndex),
    ConditionType: undefined,
    Spec: {},
  }
}

export const getEmptyBlock = (blockIndex = 0): UiBlock => {
  return {
    JoinType: getDefaultJoinType(blockIndex),
    Conditions: [getEmptyCondition()],
  }
}

const getDefaultJoinType = (index = 0): ActionType => {
  return index === 0 ? ActionType.Nil : ActionType.And
}

export const getDefaultActivityDateValue = (
  t?: UiActivityDateType,
): UiActivityDateValue | undefined => {
  switch (t) {
    case UiActivityDateType.AnyMoment:
      return null
    case UiActivityDateType.DateInterval:
      return { From: null, To: null }
    case UiActivityDateType.LastDays:
    case UiActivityDateType.LastHours:
    case UiActivityDateType.ExactDaysAgo:
    case UiActivityDateType.ExactHoursAgo:
      return 0
  }
  return undefined
}

export const getActivityDateNumberInputFollowText = (
  t: UiActivityDateType,
  intl: IntlShape,
): string => {
  switch (t) {
    case UiActivityDateType.LastDays:
      return intl.formatMessage({
        id: 'Load.Filters.ActivityDaysLastTips',
      })
    case UiActivityDateType.LastHours:
      return intl.formatMessage({
        id: 'Load.Filters.ActivityHoursLastTips',
      })
    case UiActivityDateType.ExactDaysAgo:
      return intl.formatMessage({
        id: 'Load.Filters.ActivityDaysExactlyTips',
      })
    case UiActivityDateType.ExactHoursAgo:
      return intl.formatMessage({
        id: 'Load.Filters.ActivityHoursExactlyTips',
      })
  }
  return ''
}
